/*
 * Merchant: "There isnt a way to change sizes of items when buying an item."
 *
 * Confirmed against the real catalog: most of the Shopify Collective import
 * is ordinary sized apparel (Size option, values like XS–4XL, several real
 * variants per product) — not the one-off single-piece collectible the rest
 * of this data model was built around. snippets/sat-bootstrap.liquid's
 * product record only ever exposed `variantId`, the store's own
 * `selected_or_first_available_variant`, with no other variant ever reaching
 * the client — so however many real sizes a product actually had, the app
 * could only ever add that one, and the product detail modal
 * (snippets/sat-overlays.liquid's hasDetail block) had nothing to build a
 * size picker from even if it wanted to. sat-bootstrap.liquid now also emits
 * a `variants` array (id/title/available/stock per variant) on every product
 * record; this patch is what turns that into an actual picker and makes
 * "Add To Crate" respect it.
 *
 * Selection state mirrors assets/sat-detail-gallery-patch.js's own pattern
 * exactly (detailVariantId + detailVariantFor, not detailVariantId alone) —
 * a product-id guard so switching to a *different* product's detail (e.g.
 * "From The Same Department") can't silently keep a previous product's size
 * selected against a size list it was never chosen from.
 *
 * Committing a size: `this._products` holds one persistent record per
 * product, each with its own single `variantId` that every add-to-cart path
 * in the app (this quick "Add" button on a card, the wishlist drawer, a
 * related-product row, this very modal) reads at the moment of the click —
 * there's no separate per-line variant argument anywhere to thread a
 * selection through. Rather than duplicate addToCart's whole cart-line /
 * optimistic-update / Shopify-cart-post pipeline here, addDetail (the only
 * place a shopper actually commits a size) writes the chosen variant id onto
 * that persistent record right before calling the real addToCart, and does
 * NOT put it back afterward — every read addToCart makes of `p.variantId`
 * happens synchronously before it returns (confirmed against sat-app.js:
 * the /cart/add.js request body is built inline, not inside the later
 * .then()), so nothing async ever observes the old value, and leaving the
 * pick in place is also just correct: that's genuinely the size now sitting
 * in the real Shopify cart, so it's the right thing for a stray later
 * "Add" click elsewhere on the same product to add too.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    var self = this;
    var s = this.state;

    if (!out.detail) {
      out.detailHasSizes = 'none';
      out.detailSizes = [];
      return out;
    }

    var variants = (out.detail.variants || []).filter(Boolean);
    // A product with exactly one real variant (Shopify's own "Default
    // Title" for anything with no options) has nothing to pick between —
    // leave the modal exactly as it looked before this patch.
    if (variants.length < 2) {
      out.detailHasSizes = 'none';
      out.detailSizes = [];
      return out;
    }

    var detailId = out.detail.id;
    var matchesCurrent = s.detailVariantFor === detailId &&
      variants.some(function (v) { return v.id === s.detailVariantId; });
    var selId = matchesCurrent
      ? s.detailVariantId
      : (variants.find(function (v) { return v.id === out.detail.variantId; })
        || variants.find(function (v) { return v.available; })
        || variants[0]).id;
    var chosen = variants.find(function (v) { return v.id === selId; }) || variants[0];

    out.detailHasSizes = 'flex';
    out.detailSizes = variants.map(function (v) {
      var isSel = v.id === selId;
      return {
        id: v.id,
        label: v.title,
        available: v.available,
        borderColor: isSel ? '#F0C77A' : (v.available ? 'rgba(240,199,122,.35)' : 'rgba(240,199,122,.15)'),
        bg: isSel ? '#F0C77A' : 'transparent',
        textColor: isSel ? '#2B2118' : (v.available ? '#F4EFE3' : '#5a5347'),
        cursor: v.available ? 'pointer' : 'default',
        select: v.available
          ? function () { self.setState({ detailVariantId: v.id, detailVariantFor: detailId }); }
          : function () {}
      };
    });

    // Mirror the selected size's own real availability/stock into the same
    // fields the base detail binding (sat-app.js) already computes off the
    // default variant, so the ON FILE count and the Add To Crate state track
    // whichever size is actually picked instead of always the default one.
    out.detail = Object.assign({}, out.detail, {
      soldOut: !chosen.available,
      stockLabel: chosen.available ? (chosen.stock > 0 ? chosen.stock + ' on file' : 'In Stock') : 'Sold Out',
      addLabel: chosen.available ? 'Add To Crate' : 'Sold Out',
      addBg: chosen.available ? '#A6402A' : '#5a5a52',
      addCursor: chosen.available ? 'pointer' : 'default'
    });

    out.addDetail = function () {
      if (!chosen.available) { return; }
      var real = self._products.find(function (p) { return p.id === detailId; });
      if (!real) { return; }
      real.variantId = chosen.id;
      self.addToCart(detailId, 1);
      self.setState({ detailId: null });
    };

    return out;
  };
})();
